/**
 * Owner: Masa Bayu
 * File: library-item.ts
 * Date:Time: 2026-09-15 23:21
 * Description: T4 library item domain — ownership projection from entitlement.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { isEntitlementActiveForRead } from './entitlement-boundary';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
import { assertT4E1SecurityIntact } from './library-security';
export const QXREAD_LIBRARY_ITEM_STATES = [
    'materializing',
    'available',
    'reading',
    'completed',
    'update_available',
    'offline_preparing',
    'offline_ready',
    'offline_stale',
    'blocked',
    'removed',
    'archived',
];
export const QXREAD_READING_PROGRESS_STATES = ['not_started', 'in_progress', 'completed'];
function assertLibraryState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_LIBRARY_ITEM_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_LIBRARY_ITEM_STATES.join(', ')}`);
    }
    return value;
}
function assertProgressState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_READING_PROGRESS_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_READING_PROGRESS_STATES.join(', ')}`);
    }
    return value;
}
function assertProgressPercent(value) {
    if (!Number.isFinite(value) || value < 0 || value > 100) {
        throw new Error('QXRead library progress percentComplete must be 0–100');
    }
    return value;
}
export function createEmptyReadingProgress(options) {
    return Object.freeze({
        state: 'not_started',
        percentComplete: 0,
        lastOpenedAt: options?.lastOpenedAt ?? null,
        completedAt: null,
        lastPositionRef: null,
    });
}
/**
 * Project an active entitlement into a library ownership record.
 * Does not mint entitlements, mutate QXP, or execute Reader.
 */
export function projectEntitlementToLibraryItem(entitlement, options) {
    assertNoForbiddenFoundationKey(options, 'QXRead library item');
    assertT4E1SecurityIntact();
    assertValidId(entitlement.accountId, 'QXRead library accountId');
    assertValidId(entitlement.publicationId, 'QXRead library publicationId');
    assertValidId(entitlement.entitlementId, 'QXRead library entitlementId');
    if (typeof options.title !== 'string' || options.title.trim().length === 0) {
        throw new Error('QXRead library title is required');
    }
    if (!isEntitlementActiveForRead(entitlement) &&
        entitlement.state !== 'pending_grant' &&
        entitlement.state !== 'revoked' &&
        entitlement.state !== 'expired') {
        throw new Error(`QXRead library projection blocked for entitlement state "${entitlement.state}"`);
    }
    const libraryItemId = options.libraryItemId !== undefined ? options.libraryItemId : createQxreadId('qxrdlib');
    assertValidId(libraryItemId, 'QXRead libraryItemId');
    const createdAt = options.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead library createdAt');
    let state = 'available';
    if (entitlement.state === 'pending_grant') {
        state = 'materializing';
    }
    else if (entitlement.state === 'revoked' || entitlement.state === 'expired') {
        state = 'blocked';
    }
    else if (!isEntitlementActiveForRead(entitlement)) {
        state = 'blocked';
    }
    else if (options.pinnedVersionLabel !== undefined &&
        options.pinnedVersionLabel !== null &&
        options.listedVersionLabel !== undefined &&
        options.listedVersionLabel !== null &&
        options.pinnedVersionLabel !== options.listedVersionLabel) {
        state = 'update_available';
    }
    const progress = options.progress ?? createEmptyReadingProgress();
    assertProgressState(progress.state, 'QXRead library progress state');
    assertProgressPercent(progress.percentComplete);
    return Object.freeze({
        libraryItemId,
        accountId: entitlement.accountId,
        publicationId: entitlement.publicationId,
        entitlementId: entitlement.entitlementId,
        title: options.title.trim(),
        qxpPublicationRef: options.qxpPublicationRef ?? null,
        pinnedVersionLabel: options.pinnedVersionLabel ?? null,
        listedVersionLabel: options.listedVersionLabel ?? null,
        state,
        progress,
        offlineAvailable: false,
        createdAt,
        updatedAt: createdAt,
    });
}
export function applyReadingProgress(item, progress) {
    assertT4E1SecurityIntact();
    if (item.state === 'blocked' || item.state === 'removed' || item.state === 'archived') {
        throw new Error(`QXRead reading progress blocked for library state "${item.state}"`);
    }
    const state = assertProgressState(progress.state, 'QXRead library progress state');
    const percentComplete = assertProgressPercent(progress.percentComplete);
    const lastOpenedAt = progress.lastOpenedAt !== undefined ? progress.lastOpenedAt : new Date().toISOString();
    if (lastOpenedAt !== null) {
        assertTimestamp(lastOpenedAt, 'QXRead library lastOpenedAt');
    }
    let completedAt = progress.completedAt ?? null;
    if (state === 'completed') {
        completedAt = completedAt ?? new Date().toISOString();
        assertTimestamp(completedAt, 'QXRead library completedAt');
    }
    else {
        completedAt = null;
    }
    let lastPositionRef = progress.lastPositionRef !== undefined
        ? progress.lastPositionRef
        : item.progress.lastPositionRef;
    if (lastPositionRef !== null && lastPositionRef.trim().length === 0) {
        throw new Error('QXRead library lastPositionRef must be non-empty when set');
    }
    if (lastPositionRef !== null) {
        lastPositionRef = lastPositionRef.trim();
    }
    let nextState = item.state;
    if (state === 'completed') {
        nextState = 'completed';
    }
    else if (state === 'in_progress' && item.state === 'available') {
        nextState = 'reading';
    }
    else if (state === 'in_progress') {
        nextState = item.state === 'update_available' ? 'update_available' : 'reading';
    }
    const updatedAt = new Date().toISOString();
    return Object.freeze({
        ...item,
        state: assertLibraryState(nextState, 'QXRead library item state'),
        progress: Object.freeze({
            state,
            percentComplete,
            lastOpenedAt,
            completedAt,
            lastPositionRef,
        }),
        updatedAt,
    });
}
export function blockLibraryItemForRevokedEntitlement(item, entitlement) {
    if (entitlement.entitlementId !== item.entitlementId) {
        throw new Error('QXRead library block requires matching entitlementId');
    }
    if (entitlement.state !== 'revoked' && entitlement.state !== 'expired') {
        throw new Error('QXRead library block requires revoked or expired entitlement');
    }
    return Object.freeze({
        ...item,
        state: 'blocked',
        offlineAvailable: false,
        updatedAt: new Date().toISOString(),
    });
}
export function libraryItemAllowsReaderLaunchIntent(item) {
    return (item.state === 'available' ||
        item.state === 'reading' ||
        item.state === 'completed' ||
        item.state === 'update_available' ||
        item.state === 'offline_ready');
}
//# sourceMappingURL=library-item.js.map